import { SSE_EVENTS } from "@lexicanon/shared"
import { useEffect, useRef, useState } from "react"
import type { IProgressiveVisual } from "../components/TTRPGPanel/interfaces"
import type { IDMCoachingRequest, IDMParticipation, IDMSuggestion, IPlayerCoaching } from "../lib/types/events"
import type { ITrackedMeeting } from "../lib/types/streaming"
import { useAuth } from "./useAuth"

export type { IDMCoachingRequest, IDMParticipation, IDMSuggestion, IPlayerCoaching, IProgressiveVisual }

function isString(v: unknown): v is string {
  return typeof v === "string"
}

/** Exponential-backoff delays (ms) mirroring the recorder WS reconnect ladder. */
const SSE_RECONNECT_DELAYS_MS = [2_000, 5_000, 10_000, 20_000, 40_000]

/**
 * How long the socket can be down before callers hear about it. The browser
 * fires `onerror` the instant a connection drops — including while it is still
 * auto-retrying (`CONNECTING`) — so announcing immediately flashes "offline"
 * on every remount and every brief blip (for example, coming back from a
 * recording). Callers should start optimistic and only show a pause after this.
 */
const OFFLINE_NOTICE_MS = 10_000

/**
 * Opens a self-healing EventSource. On `error` the browser either auto-retries
 * (`CONNECTING`) or gives up (`CLOSED`). A drop does not announce offline until
 * {@link OFFLINE_NOTICE_MS} has passed without `onopen`. A socket that stays
 * closed, or stuck connecting, is replaced on the backoff ladder. Returns a
 * teardown that cancels pending timers and closes the live socket.
 */
export function createReconnectingEventSource(
  url: string,
  withCredentials: boolean,
  onMessage: (event: MessageEvent<string>) => void,
  onStatus?: (connected: boolean) => void,
): () => void {
  let es: EventSource | null = null
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null
  let offlineTimer: ReturnType<typeof setTimeout> | null = null
  let attempts = 0
  let disposed = false
  let replacing = false
  let announcedOffline = false

  const clearOfflineTimer = (): void => {
    if (offlineTimer !== null) {
      clearTimeout(offlineTimer)
      offlineTimer = null
    }
  }

  const scheduleOfflineNotice = (): void => {
    if (announcedOffline || offlineTimer !== null) return
    offlineTimer = setTimeout(() => {
      offlineTimer = null
      announcedOffline = true
      onStatus?.(false)
    }, OFFLINE_NOTICE_MS)
  }

  const markOnline = (): void => {
    clearOfflineTimer()
    if (reconnectTimer !== null) {
      clearTimeout(reconnectTimer)
      reconnectTimer = null
    }
    announcedOffline = false
    attempts = 0
    onStatus?.(true)
  }

  const connect = (): void => {
    if (disposed) return
    es = new EventSource(url, { withCredentials })
    // A socket that never opens (and never errors) still counts as offline.
    scheduleOfflineNotice()

    es.onopen = (): void => {
      markOnline()
    }

    es.onmessage = onMessage

    es.onerror = (): void => {
      if (disposed || replacing) return
      const current = es
      // An open socket can emit a spurious error. Leave it alone.
      if (current && current.readyState === EventSource.OPEN) return
      scheduleOfflineNotice()
      if (reconnectTimer !== null) return
      const delay = SSE_RECONNECT_DELAYS_MS[Math.min(attempts, SSE_RECONNECT_DELAYS_MS.length - 1)]
      attempts += 1
      reconnectTimer = setTimeout(() => {
        reconnectTimer = null
        if (disposed) return
        if (current && current.readyState === EventSource.OPEN) return
        replacing = true
        current?.close()
        if (es === current) es = null
        replacing = false
        connect()
      }, delay)
    }
  }

  connect()

  return (): void => {
    disposed = true
    replacing = true
    clearOfflineTimer()
    if (reconnectTimer !== null) {
      clearTimeout(reconnectTimer)
      reconnectTimer = null
    }
    es?.close()
    es = null
  }
}

function isTrackedMeeting(v: unknown): v is ITrackedMeeting {
  return typeof v === "object" && v !== null && "meetingId" in v && "userId" in v && "phase" in v
}

export function useMeetingEvents(
  onUpdate: (meeting: ITrackedMeeting) => void,
  onStage?: (meetingId: string, stage: string, progress?: number) => void,
  /** Per-DAG-step pipeline events (`pipeline_started` roster + `pipeline_step`
   * running/completed/skipped/failed) — drives the finalize bar's real step
   * progress + current-step subtitle. */
  onPipeline?: (meetingId: string, payload: Record<string, unknown>) => void,
  /** Broadcast after ANY speaker mutation (rename/discard/profile/hypothesis
   * confirm-reject/pipeline resolution). Thin `{ speakers_version, reason }`
   * payload; the host invalidates its meeting + transcript queries on receipt so
   * every surface reconverges without a reload. */
  onSpeakersUpdated?: (meetingId: string, speakersVersion: number, reason: string) => void,
): boolean {
  // Start optimistic. The socket reports offline only after it has been down
  // for OFFLINE_NOTICE_MS, so a fresh dashboard mount does not flash "offline"
  // while the first EventSource is still opening.
  const [connected, setConnected] = useState(true)
  const callbackRef = useRef(onUpdate)
  callbackRef.current = onUpdate
  const stageCallbackRef = useRef(onStage)
  stageCallbackRef.current = onStage
  const pipelineCallbackRef = useRef(onPipeline)
  pipelineCallbackRef.current = onPipeline
  const speakersUpdatedCallbackRef = useRef(onSpeakersUpdated)
  speakersUpdatedCallbackRef.current = onSpeakersUpdated
  const { user } = useAuth()
  const companyId = user?.activeOrganizationId

  useEffect(() => {
    if (!companyId) return

    const handleMessage = (event: MessageEvent<string>): void => {
      try {
        const data: unknown = JSON.parse(event.data)
        if (
          typeof data === "object" &&
          data !== null &&
          "type" in data &&
          (data as Record<string, unknown>).type === SSE_EVENTS.PROCESSING_STAGE &&
          "meetingId" in data &&
          stageCallbackRef.current
        ) {
          const rec = data as Record<string, unknown>
          const meetingId = isString(rec.meetingId) ? rec.meetingId : String(rec.meetingId)
          const stage = isString(rec.stage) ? rec.stage : ""
          // Forward the persisted 0-100 progress only when the backend sent a
          // real number, so callers that don't want it (and the existing 2-arg
          // contract) stay untouched.
          if (typeof rec.progress === "number") {
            stageCallbackRef.current(meetingId, stage, rec.progress)
          } else {
            stageCallbackRef.current(meetingId, stage)
          }
          return
        }
        if (
          typeof data === "object" &&
          data !== null &&
          "type" in data &&
          ((data as Record<string, unknown>).type === SSE_EVENTS.PIPELINE_STARTED ||
            (data as Record<string, unknown>).type === SSE_EVENTS.PIPELINE_STEP) &&
          "meetingId" in data &&
          pipelineCallbackRef.current
        ) {
          const rec = data as Record<string, unknown>
          const meetingId = isString(rec.meetingId) ? rec.meetingId : String(rec.meetingId)
          pipelineCallbackRef.current(meetingId, rec)
          return
        }
        if (
          typeof data === "object" &&
          data !== null &&
          "type" in data &&
          (data as Record<string, unknown>).type === SSE_EVENTS.SPEAKERS_UPDATED &&
          "meetingId" in data &&
          speakersUpdatedCallbackRef.current
        ) {
          const rec = data as Record<string, unknown>
          const meetingId = isString(rec.meetingId) ? rec.meetingId : String(rec.meetingId)
          const speakersVersion =
            typeof rec.speakers_version === "number" ? rec.speakers_version : Number(rec.speakers_version)
          const reason = isString(rec.reason) ? rec.reason : ""
          speakersUpdatedCallbackRef.current(meetingId, speakersVersion, reason)
          return
        }
        // Only forward TrackedMeeting objects, not streaming events
        if (!isTrackedMeeting(data)) return
        const rec = data as unknown as Record<string, unknown>
        if (isString(rec.type) && (rec.type.startsWith("streaming_") || rec.type === SSE_EVENTS.PROGRESSIVE_SUMMARY))
          return
        callbackRef.current(data)
      } catch {
        // ignore parse errors
      }
    }

    return createReconnectingEventSource(`/api/companies/${companyId}/events`, false, handleMessage, setConnected)
  }, [companyId])

  return connected
}

/**
 * Subscribes to the company event stream and fires `onSuggested` whenever the
 * pipeline emits a JARGON_SUGGESTED event — i.e. a processed meeting produced
 * fresh domain-term candidates. The Jargon Manager uses this to refill its
 * review queue live, without a manual refresh. Best-effort: parse errors are
 * ignored and the socket self-heals via createReconnectingEventSource. Returns
 * the live connection status.
 */
export function useJargonSuggestedEvents(onSuggested: () => void): boolean {
  const [connected, setConnected] = useState(false)
  const callbackRef = useRef(onSuggested)
  callbackRef.current = onSuggested
  const { user } = useAuth()
  const companyId = user?.activeOrganizationId

  useEffect(() => {
    if (!companyId) return

    const handleMessage = (event: MessageEvent<string>): void => {
      try {
        const data: unknown = JSON.parse(event.data)
        if (typeof data === "object" && data !== null && "type" in data && data.type === SSE_EVENTS.JARGON_SUGGESTED) {
          callbackRef.current()
        }
      } catch {
        // ignore parse errors
      }
    }

    return createReconnectingEventSource(`/api/companies/${companyId}/events`, false, handleMessage, setConnected)
  }, [companyId])

  return connected
}

export function useTTRPGEvents(meetingId: string): {
  dmSuggestion: IDMSuggestion | null
  playerCoaching: IPlayerCoaching | null
  currentVisual: IProgressiveVisual | null
  participationData: IDMParticipation | null
  dmCoachingRequest: IDMCoachingRequest | null
} {
  const [dmSuggestion, setDmSuggestion] = useState<IDMSuggestion | null>(null)
  const [playerCoaching, setPlayerCoaching] = useState<IPlayerCoaching | null>(null)
  const [currentVisual, setCurrentVisual] = useState<IProgressiveVisual | null>(null)
  const [participationData, setParticipationData] = useState<IDMParticipation | null>(null)
  const [dmCoachingRequest, setDmCoachingRequest] = useState<IDMCoachingRequest | null>(null)
  const { user } = useAuth()
  const companyId = user?.activeOrganizationId

  useEffect(() => {
    if (!companyId || !meetingId) return

    const handleMessage = (event: MessageEvent<string>): void => {
      try {
        const parsed: unknown = JSON.parse(event.data)
        if (typeof parsed !== "object" || parsed === null) return
        const data = parsed as Record<string, unknown>
        if (data.meetingId !== meetingId) return

        if (data.type === SSE_EVENTS.DM_SUGGESTION) {
          setDmSuggestion({
            category: isString(data.category) ? data.category : "",
            suggestion: isString(data.suggestion) ? data.suggestion : "",
            timestamp: isString(data.timestamp) ? data.timestamp : "",
            targetPlayer: isString(data.targetPlayer) ? data.targetPlayer : undefined,
            participationPct: typeof data.participationPct === "number" ? data.participationPct : undefined,
            storyWhy: isString(data.storyWhy) ? data.storyWhy : undefined,
            involvedCharacter: isString(data.involvedCharacter)
              ? data.involvedCharacter
              : data.involvedCharacter === null
                ? null
                : undefined,
          })
          return
        }

        if (data.type === SSE_EVENTS.DM_PARTICIPATION) {
          const speakers = data.speakers
          if (typeof speakers === "object" && speakers !== null) {
            setParticipationData({ speakers: speakers as Record<string, number> })
          }
          return
        }

        if (data.type === SSE_EVENTS.DM_COACHING_REQUEST) {
          const hooksRaw = data.hooks
          if (Array.isArray(hooksRaw)) {
            const hooks = hooksRaw
              .filter((h): h is Record<string, unknown> => typeof h === "object" && h !== null)
              .map((h) => ({
                suggestion: isString(h.suggestion) ? h.suggestion : "",
                why: isString(h.why) ? h.why : "",
                character: isString(h.character) ? h.character : null,
              }))
            setDmCoachingRequest({ hooks })
          }
          return
        }

        if (data.type === SSE_EVENTS.PLAYER_COACHING) {
          // The payload re-uses "type" for the coaching sub-type (action|roleplay|interaction).
          // The outer discriminator is also "type" === SSE_EVENTS.PLAYER_COACHING, so we read it from
          // a dedicated field if present, falling back to the outer type value.
          const coachingType = isString(data.coachingType) ? data.coachingType : isString(data.type) ? data.type : ""
          setPlayerCoaching({
            suggestion: isString(data.suggestion) ? data.suggestion : "",
            type: coachingType,
            characterId: isString(data.characterId) ? data.characterId : null,
            timestamp: isString(data.timestamp) ? data.timestamp : "",
          })
          return
        }

        if (data.type === SSE_EVENTS.VISUAL_PROGRESSIVE) {
          setCurrentVisual({
            artifactId: isString(data.artifactId) ? data.artifactId : undefined,
            url: isString(data.url) ? data.url : "",
            prompt: isString(data.prompt) ? data.prompt : "",
            timestamp: isString(data.timestamp) ? data.timestamp : "",
            trigger:
              data.trigger === "character_intro" || data.trigger === "scene_change" || data.trigger === "action"
                ? data.trigger
                : undefined,
            triggerDescription: isString(data.triggerDescription) ? data.triggerDescription : undefined,
            characters_involved: Array.isArray(data.characters_involved)
              ? data.characters_involved.filter(isString)
              : undefined,
            reference_images: Array.isArray(data.reference_images) ? data.reference_images.filter(isString) : undefined,
            location: isString(data.location) ? data.location : data.location === null ? null : undefined,
          })
        }
      } catch {
        // ignore parse errors
      }
    }

    return createReconnectingEventSource(`/api/companies/${companyId}/events`, false, handleMessage)
  }, [companyId, meetingId])

  return { dmSuggestion, playerCoaching, currentVisual, participationData, dmCoachingRequest }
}
